import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { normalizeDigits } from '@/lib/phone'
import { useI18n } from '@/lib/i18n'

interface QuantityStepperProps {
    value: number
    onChange: (value: number) => void
    min?: number
    max?: number | null
    size?: 'sm' | 'md' | 'lg'
    label: string
    disabled?: boolean
    className?: string
}

export const QuantityStepper: FC<QuantityStepperProps> = ({ value, onChange, min = 1, max = null, size = 'md', label, disabled, className }): ReactNode => {
    const { tx } = useI18n()
    const [draft, setDraft] = useState(String(value))

    useEffect(() => {
        setDraft(String(value))
    }, [value])

    const clamp = (next: number): number => {
        let result = Math.floor(Number.isFinite(next) ? next : min)

        if (result < min) result = min
        if (max !== null && max !== undefined && result > max) result = Math.max(min, max)

        return result
    }

    const commit = (): void => {
        const parsed = Number(normalizeDigits(draft).replace(/[^\d]/g, ''))
        const next = clamp(parsed || min)

        setDraft(String(next))

        if (next !== value) onChange(next)
    }

    const heights = { sm: 'h-9', md: 'h-11', lg: 'h-13' }
    const buttons = { sm: 'w-9', md: 'w-11', lg: 'w-12' }
    const atMax = max !== null && max !== undefined && value >= max

    return (
        <div className={cn('inline-flex items-stretch overflow-hidden rounded-full border border-navy-200 bg-white', heights[size], disabled && 'opacity-60', className)}>
            <button
                type='button'
                onClick={() => onChange(clamp(value - 1))}
                disabled={disabled || value <= min}
                className={cn('flex items-center justify-center text-navy-800 transition-colors hover:bg-navy-50 disabled:cursor-not-allowed disabled:text-navy-300', buttons[size])}
                aria-label={tx('تقليل الكمية', 'Decrease quantity')}
            >
                <Minus className='size-4' />
            </button>

            <input
                type='text'
                inputMode='numeric'
                value={draft}
                disabled={disabled}
                onChange={event => setDraft(event.target.value)}
                onBlur={commit}
                onKeyDown={event => {
                    if (event.key === 'Enter') {
                        event.preventDefault()
                        commit()
                    }
                }}
                aria-label={label}
                className='w-14 min-w-0 border-x border-navy-100 bg-transparent text-center text-[15px] font-semibold tabular-nums text-navy-950 focus:bg-navy-50/60 focus:outline-none'
            />

            <button
                type='button'
                onClick={() => onChange(clamp(value + 1))}
                disabled={disabled || atMax}
                className={cn('flex items-center justify-center text-navy-800 transition-colors hover:bg-navy-50 disabled:cursor-not-allowed disabled:text-navy-300', buttons[size])}
                aria-label={tx('زيادة الكمية', 'Increase quantity')}
            >
                <Plus className='size-4' />
            </button>
        </div>
    )
}
